import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form as AntdForm } from 'antd';
import { useSelector } from 'react-redux';
import { routes } from '../../../../../utils/helpers/ApiClient';
import AppBarCMS from '../../../../basic/Navigation/AppBarCMS/AppBarCMS';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';
import { AdminContentBlock, Spinner } from '../../../../common';
import Form from '../../../../antd/form/Form/Form';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import { applyPhdValidationErrors } from '../utils/phdApiFeedback.js';
import {
  useGetPhdIndividualPlanQuery,
  useSavePhdIndividualPlanMutation,
  useLazyGetPhdIndividualPlanDocumentQuery,
  useArchivePhdIndividualPlanDocumentMutation,
  useGetPhdIndividualPlanSubjectsQuery,
  useSavePhdIndividualPlanTaskMutation,
} from '../../../../../redux/services/phd';
import { usePhdReferenceData } from '../usePhdReferenceData.jsx';
import PhdIndividualPlansEditForm from './PhdIndividualPlansEditForm.jsx';
import usePhdStudentProfile, {
  mapStudentProfileToFormDefaults,
} from '../usePhdStudentProfile.jsx';
import { downloadBlobResponse } from '../../../../../utils/helpers/downloadBlobResponse';
import PhdIndividualPlanSubjectsSection from './PhdIndividualPlanSubjectsSection.jsx';
import PhdIndividualPlanTasksSection from './PhdIndividualPlanTasksSection.jsx';
import useIsUserPhdAdmin from '../../../../../hooks/useIsUserPhdAdmin.jsx';
import { usePhdDashboardPrefill } from '../utils/usePhdDashboardPrefill.js';
import { usePhdDirtyLeaveGuard } from '../usePhdDirtyLeaveGuard.jsx';
import PhdArchivedRecordNotice from '../PhdArchivedRecordNotice.jsx';

const dateFields = [
  'date_dissertation_application',
  'date_dissertation_submission',
  'date_study_start',
  'date_study_end',
];

const toDayjs = (value) => (value ? dayjs(value) : null);
const currentYear = () => dayjs().year();
const buildLegacyDateDefaults = () => {
  const year = currentYear();

  return {
    date_study_start: dayjs(new Date(year, 8, 1)),
    date_dissertation_application: dayjs(new Date(year + 1, 8, 6)),
    date_dissertation_submission: dayjs(new Date(year + 3, 3, 1)),
    date_study_end: dayjs(new Date(year + 3, 7, 31)),
  };
};

const PhdIndividualPlansEdit = ({ mode }) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = AntdForm.useForm();
  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const user = useSelector((state) => state.auth.user);
  const isAdmin = useIsUserPhdAdmin();
  const dashboardPrefill = usePhdDashboardPrefill();
  const pageTitle = isCreate
    ? t('Pridanie individuálneho plánu')
    : t('Editácia individuálneho plánu');

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  const { data: detail, isLoading } = useGetPhdIndividualPlanQuery(id, {
    skip: !isEdit,
  });
  const [savePlan, { isLoading: isSaving }] =
    useSavePhdIndividualPlanMutation();
  const [downloadPlanDoc, { isLoading: isDownloading }] =
    useLazyGetPhdIndividualPlanDocumentQuery();
  const [archivePlanDoc, { isLoading: isArchiving }] =
    useArchivePhdIndividualPlanDocumentMutation();
  const { data: planSubjectsData, isFetching: isLoadingPlanSubjects } =
    useGetPhdIndividualPlanSubjectsQuery(
      isEdit && detail?.id ? { phd_individual_plan_id: detail.id } : undefined,
      { skip: !isEdit || !detail?.id },
    );
  const [savePlanTask, { isLoading: isSavingPlanTask }] =
    useSavePhdIndividualPlanTaskMutation();
  const { saveSuccess, saveFailed } = useToastMessages();
  const { data: studentProfile, isFetching: isStudentLoading } =
    usePhdStudentProfile();
  const isStudentContext = Boolean(studentProfile?.id) && !isAdmin;
  const [selectedElectiveSubjectIds, setSelectedElectiveSubjectIds] = useState(
    [],
  );
  const [draftTasks, setDraftTasks] = useState([]);

  const {
    studentDisplayNameMap,
    schoolYearOptions,
    students,
    schoolYears,
    ensureSchoolYearSelection,
    isCreatingSchoolYear,
    isLoading: isReferenceLoading,
  } = usePhdReferenceData({
    includeStudents: !isStudentContext,
    studentsRequireThesis: true,
  });

  const initials = useMemo(() => {
    if (isCreate) {
      const studentDefaults = mapStudentProfileToFormDefaults(studentProfile);
      const legacyDateDefaults = buildLegacyDateDefaults();

      return {
        ...studentDefaults,
        phd_student_id:
          dashboardPrefill.phd_student_id ??
          studentDefaults.phd_student_id ??
          undefined,
        ...legacyDateDefaults,
        thematic_areas: undefined,
        thesis_title: studentDefaults.thesis_title ?? undefined,
        thesis_required_literature: undefined,
        thesis_recommended_literature: undefined,
        thesis_recommended_lectures: undefined,
      };
    }

    if (!detail) {
      return null;
    }

    const mapped = { ...detail };
    dateFields.forEach((field) => {
      mapped[field] = toDayjs(detail[field]);
    });
    return mapped;
  }, [dashboardPrefill, detail, isCreate, studentProfile]);

  const planSubjectRows = useMemo(
    () =>
      (planSubjectsData || []).filter(
        (row) => row?.phd_individual_plan_id === detail?.id,
      ),
    [detail?.id, planSubjectsData],
  );
  const electivePlanSubjectRows = useMemo(
    () =>
      planSubjectRows.filter(
        (row) => row?.study_subject?.completion_type !== 'T',
      ),
    [planSubjectRows],
  );

  useEffect(() => {
    if (!isEdit || !detail?.id) {
      return;
    }
    setSelectedElectiveSubjectIds(
      electivePlanSubjectRows.map((row) => row.phd_study_subject_id),
    );
  }, [isEdit, detail?.id, electivePlanSubjectRows]);

  const { modal, onSaveFailed, onSaveSuccess, onValuesChange } =
    usePhdDirtyLeaveGuard(form, initials);

  const handleSubmit = async (values) => {
    const payload = {
      ...detail,
      ...values,
      elective_subject_ids: selectedElectiveSubjectIds,
    };
    dateFields.forEach((field) => {
      payload[field] = values[field]
        ? values[field].format('YYYY-MM-DD')
        : null;
    });

    try {
      const savedPlan = await savePlan({
        id: isEdit ? detail.id : undefined,
        body: payload,
      }).unwrap();
      const planId = isEdit ? detail?.id : savedPlan?.id;
      if (isCreate && planId && draftTasks.length > 0) {
        await Promise.all(
          draftTasks.map((task) =>
            savePlanTask({
              body: {
                phd_individual_plan_id: planId,
                task: task.task,
                date_deadline: task.date_deadline || null,
              },
            }).unwrap(),
          ),
        );
      }

      saveSuccess();
      onSaveSuccess(() => navigate(routes.cmsPhdIndividualPlans));
    } catch (error) {
      onSaveFailed();
      applyPhdValidationErrors(form, error);
      saveFailed(error);
    }
  };

  const handleDownload = async () => {
    if (!detail?.id) return;
    try {
      const response = detail.is_frozen
        ? await downloadPlanDoc(detail.id).unwrap()
        : await archivePlanDoc(detail.id).unwrap();
      downloadBlobResponse(
        response,
        `${detail.student_display_name || t('Individuálny plán')}.docx`,
      );
    } catch (error) {
      saveFailed(error);
    }
  };

  const mainActions = useMemo(() => {
    if (!isEdit || !detail) return [];
    return [
      {
        onClick: handleDownload,
        title: detail.is_frozen
          ? t('Stiahnuť DOCX')
          : isAdmin
            ? t('Vydať a stiahnuť archívny DOCX')
            : t('Archívnu verziu môže vydať iba administrátor'),
        icon: <i className="fa fa-download" />,
        permission: 'phd-individual-plans.index',
        disabled: isDownloading || isArchiving || (!detail.is_frozen && !isAdmin),
      },
    ];
  }, [detail, handleDownload, isAdmin, isArchiving, isDownloading, isEdit, t]);

  const isSyncingSubjects = isSavingPlanTask;
  const isReadOnly =
    detail?.is_read_only === true || detail?.is_canonical === false;

  if (
    (isEdit && isLoading) ||
    isReferenceLoading ||
    !initials ||
    isStudentLoading ||
    (isEdit && isLoadingPlanSubjects)
  ) {
    return <Spinner />;
  }

  return (
    <>
      {!isReadOnly && modal}
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsPhdIndividualPlans}
        backUrlTooltip={t('Individuálne plány')}
        permissionsBasic="phd-individual-plans"
        mainActions={mainActions}
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako funguje individuálny študijný plán?')}
          description={t(
            'Vyberte doktoranda s účinnou témou, skontrolujte povinné predmety a doplňte voliteľné predmety a úlohy. Pôvodný školský rok označuje vznik plánu, nie jeho každoročnú kópiu.',
          )}
        />
        <AdminContentBlock
          icon="fa fa-sticky-note-o"
          link={routes.cmsPhdIndividualPlans}
          title={pageTitle}
        >
          <PhdArchivedRecordNotice record={detail} />
          <Form
            form={form}
            initialValues={initials}
            onFinish={handleSubmit}
            onValuesChange={onValuesChange}
            disabled={isReadOnly}
          >
            <PhdIndividualPlansEditForm
              studentDisplayNameMap={studentDisplayNameMap}
              students={isStudentContext ? [studentProfile] : students}
              schoolYears={schoolYears}
              currentUserPersonId={user?.person_id}
              isAdmin={isAdmin}
              isEdit={isEdit}
              currentPlanId={detail?.id}
              schoolYearOptions={schoolYearOptions}
              ensureSchoolYearSelection={ensureSchoolYearSelection}
              isCreatingSchoolYear={isCreatingSchoolYear}
              isSubmitting={isSaving || isSyncingSubjects}
              isStudentContext={isStudentContext}
              isReadOnly={isReadOnly}
              beforeSubmitContent={
                (isEdit && detail?.id) || isCreate ? (
                  <>
                    <PhdIndividualPlanSubjectsSection
                      students={isStudentContext ? [studentProfile] : students}
                      value={selectedElectiveSubjectIds}
                      onChange={setSelectedElectiveSubjectIds}
                      readOnly={isStudentContext || isReadOnly}
                      preloadFromThesis={isCreate}
                    />
                    <PhdIndividualPlanTasksSection
                      planId={detail?.id}
                      value={isCreate ? draftTasks : undefined}
                      onChange={isCreate ? setDraftTasks : undefined}
                      readOnly={isStudentContext || isReadOnly}
                    />
                  </>
                ) : null
              }
            />
          </Form>
        </AdminContentBlock>
      </div>
    </>
  );
};

export default PhdIndividualPlansEdit;
